/**
 * Dono do access token do admin.
 *
 * O token vive só em memória: nada de JWT no localStorage, porque é de lá que o
 * infostealer colhe. Ao abrir a página não há nada para carregar — o token é forjado
 * na hora, assinando uma prova com a chave privada non-extractable do IndexedDB
 * (ver deviceKey.ts). Quem copia o disco não leva credencial reutilizável.
 */

import { DEFAULT_API_ADMIN, SELECTED_CHAIN_KEY, USER_KEY } from '../constants';
import { clearSession, generateDeviceKey, loadSession, saveSession, signProof } from './deviceKey';

/**
 * Renova com folga para a troca nunca cair no meio de uma navegação, mas nunca com
 * folga maior que um quinto da vida do token: com margem fixa, um TTL curto (1m, para
 * testar o refresh em dev) daria folga negativa e cairia no piso do timer, renovando a
 * cada 10 segundos.
 */
const RENEW_MARGIN_MS = 5 * 60 * 1000;
const RENEW_MARGIN_RATIO = 0.2;
const EXPIRY_SKEW_MS = 10 * 1000;
const MIN_TIMER_MS = 10 * 1000;

interface SessionTokenResponse {
  accessToken: string;
  expiresIn: number;
  me?: Record<string, unknown>;
  sessionId?: string;
}

let accessToken: string | null = null;
let expiresAt = 0;
let lifetimeMs = 0;
let pending: Promise<string | null> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

/** Síncrono de propósito: é o que o `getHeaders` do request.ts consome. */
export const getAccessToken = () => accessToken;

const decodePayload = (token: string): Record<string, unknown> => {
  try {
    const raw = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(raw));
  } catch {
    return {};
  }
};

/**
 * Permissões da UI saem do próprio token, não de uma cópia no localStorage: não há
 * como divergir do que o servidor vai aplicar. Esconder botão não é decisão de
 * segurança — o backend confere o scope de novo em cada rota.
 */
export const getScope = (): string[] => {
  if (!accessToken) { return []; }
  const scope = decodePayload(accessToken).scope;
  return Array.isArray(scope) ? scope as string[] : [];
};

export const getSessionId = (): string | null => {
  if (!accessToken) { return null; }
  const sid = decodePayload(accessToken).sid;
  return typeof sid === 'string' ? sid : null;
};

function scheduleRenew() {
  if (timer) { clearTimeout(timer); }
  const margin = Math.min(RENEW_MARGIN_MS, Math.floor(lifetimeMs * RENEW_MARGIN_RATIO));
  const delay = Math.max(expiresAt - Date.now() - margin, MIN_TIMER_MS);
  timer = setTimeout(() => { void renew(); }, delay);
}

function apply(body: SessionTokenResponse) {
  accessToken = body.accessToken;
  lifetimeMs = body.expiresIn * 1000;
  expiresAt = Date.now() + lifetimeMs;
  if (body.me) {
    // Só perfil para a UI (nome, rede, flags). O token não entra aqui.
    localStorage.setItem(USER_KEY, JSON.stringify(body.me));
  }
  scheduleRenew();
  return accessToken;
}

function dropSessionState() {
  accessToken = null;
  expiresAt = 0;
  lifetimeMs = 0;
  if (timer) { clearTimeout(timer); timer = null; }
}

async function requestNewAccessToken(): Promise<string | null> {
  const record = await loadSession();
  if (!record) { return null; }

  const proof = await signProof(record.privateKey, {
    sid: record.sessionId,
    iat: Math.floor(Date.now() / 1000),
    jti: crypto.randomUUID(),
  });

  const response = await fetch(`${DEFAULT_API_ADMIN}/employees/session/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId: record.sessionId, proof }),
  });

  // Sessão morta (revogada, expirada, funcionário inativo) manda ao login. Falha de
  // rede ou 5xx NÃO desloga: estoura e a próxima tentativa resolve.
  if (response.status === 401 || response.status === 403) {
    await clearSession();
    dropSessionState();
    return null;
  }
  if (!response.ok) {
    throw new Error(`renew failed: ${response.status}`);
  }

  return apply(await response.json());
}

/** Single-flight: sem isto, dez componentes montando juntos disparam dez forjas. */
export function renew(): Promise<string | null> {
  if (!pending) {
    pending = requestNewAccessToken().finally(() => { pending = null; });
  }
  return pending;
}

export function ensure(): Promise<string | null> {
  if (accessToken && Date.now() < expiresAt - EXPIRY_SKEW_MS) {
    return Promise.resolve(accessToken);
  }
  return renew();
}

/**
 * Descarta o token que tomou 401. A comparação evita que dois 401 em paralelo joguem
 * fora um token recém-forjado pelo primeiro.
 */
export function invalidate(used: string | null) {
  if (used && used === accessToken) {
    accessToken = null;
    expiresAt = 0;
  }
}

/**
 * Cadastra este navegador como dispositivo da sessão. Gera o par de chaves e manda
 * apenas a pública; a privada fica no IndexedDB e nunca trafega.
 */
export async function enroll(loginToken: string) {
  // O IndexedDB guarda uma chave só: o `saveSession` abaixo sobrescreve a anterior e
  // deixaria aquela sessão ativa e sem dono. O id vai junto para o servidor revogá-la.
  const replaced = await loadSession();
  const { privateKey, publicJwk } = await generateDeviceKey();

  const response = await fetch(`${DEFAULT_API_ADMIN}/employees/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: loginToken },
    body: JSON.stringify({
      publicJwk,
      deviceName: navigator.userAgent.slice(0, 256),
      replacedSessionId: replaced?.sessionId ?? null,
    }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body.message || `enroll failed: ${response.status}`);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const body: SessionTokenResponse = await response.json();
  await saveSession({ sessionId: body.sessionId as string, privateKey });
  // Sessão nova = usuário possivelmente outro. A rede escolhida pelo anterior não pode
  // sobreviver ao login, senão um funcionário de posto herda a rede que o admin deixou
  // selecionada neste navegador.
  localStorage.removeItem(SELECTED_CHAIN_KEY);
  return apply(body);
}

export async function logout() {
  const record = await loadSession();
  if (record && accessToken) {
    void fetch(`${DEFAULT_API_ADMIN}/employees/session/${record.sessionId}`, {
      method: 'DELETE',
      headers: { Authorization: accessToken },
    }).catch(() => { /* revogar é best-effort: o logout local não espera a rede */ });
  }
  await clearSession();
  dropSessionState();
}
